/**
 * Calculator Metadata
 * Central configuration for all calculators on the platform
 */

export interface Calculator {
  id: string;
  slug: string;
  titleKey: string;
  descriptionKey: string;
  category: 'property' | 'savings' | 'debt';
  icon: string;
  color: string;
  popularity: number; // 1-5, for sorting
  path: string;
  isNew?: boolean;
  comingSoon?: boolean;
}

export const calculatorCategories = {
  property: {
    key: 'property',
    titleKey: 'categories.property.title',
    icon: '🏠',
    color: '#4F46E5'
  },
  savings: {
    key: 'savings',
    titleKey: 'categories.savings.title',
    icon: '💰',
    color: '#10b981'
  },
  debt: {
    key: 'debt',
    titleKey: 'categories.debt.title',
    icon: '💳',
    color: '#f59e0b'
  }
};

export const calculators: Calculator[] = [
  // Property & Loans
  {
    id: 'house-loan',
    slug: 'house-loan',
    titleKey: 'calculators.houseLoan.title',
    descriptionKey: 'calculators.houseLoan.description',
    category: 'property',
    icon: '🏡',
    color: '#4F46E5',
    popularity: 5,
    path: '/calculators/house-loan',
  },
  {
    id: 'partial-repayment',
    slug: 'partial-repayment',
    titleKey: 'calculators.partialRepayment.title',
    descriptionKey: 'calculators.partialRepayment.description',
    category: 'property',
    icon: '💸',
    color: '#4F46E5',
    popularity: 5,
    path: '/calculators/partial-repayment',
  },
  {
    id: 'loan-affordability',
    slug: 'loan-affordability',
    titleKey: 'calculators.loanAffordability.title',
    descriptionKey: 'calculators.loanAffordability.description',
    category: 'property',
    icon: '🏦',
    color: '#4F46E5',
    popularity: 4,
    path: '/calculators/loan-affordability',
  },
  {
    id: 'rent-vs-buy',
    slug: 'rent-vs-buy',
    titleKey: 'calculators.rentVsBuy.title',
    descriptionKey: 'calculators.rentVsBuy.description',
    category: 'property',
    icon: '🏘️',
    color: '#4F46E5',
    popularity: 4,
    path: '/calculators/rent-vs-buy',
  },

  // Savings & Investment
  {
    id: 'savings-goal',
    slug: 'savings-goal',
    titleKey: 'calculators.savingsGoal.title',
    descriptionKey: 'calculators.savingsGoal.description',
    category: 'savings',
    icon: '🎯',
    color: '#10b981',
    popularity: 5,
    path: '/calculators/savings-goal',
  },
  {
    id: 'investment-return',
    slug: 'investment-return',
    titleKey: 'calculators.investmentReturn.title',
    descriptionKey: 'calculators.investmentReturn.description',
    category: 'savings',
    icon: '📈',
    color: '#10b981',
    popularity: 4,
    path: '/calculators/investment-return',
  },
  {
    id: 'retirement-planning',
    slug: 'retirement-planning',
    titleKey: 'calculators.retirementPlanning.title',
    descriptionKey: 'calculators.retirementPlanning.description',
    category: 'savings',
    icon: '🏖️',
    color: '#10b981',
    popularity: 4,
    path: '/calculators/retirement-planning',
  },

  // Debt Management
  {
    id: 'debt-payoff',
    slug: 'debt-payoff',
    titleKey: 'calculators.debtPayoff.title',
    descriptionKey: 'calculators.debtPayoff.description',
    category: 'debt',
    icon: '🔓',
    color: '#f59e0b',
    popularity: 5,
    path: '/calculators/debt-payoff',
  },
  {
    id: 'credit-card-payoff',
    slug: 'credit-card-payoff',
    titleKey: 'calculators.creditCardPayoff.title',
    descriptionKey: 'calculators.creditCardPayoff.description',
    category: 'debt',
    icon: '💳',
    color: '#f59e0b',
    popularity: 4,
    path: '/calculators/credit-card-payoff',
  },
];

export const getCalculatorBySlug = (slug: string): Calculator | undefined => {
  return calculators.find(calc => calc.slug === slug);
};

export const getCalculatorsByCategory = (category: Calculator['category']): Calculator[] => {
  return calculators.filter(calc => calc.category === category);
};

export const getPopularCalculators = (limit: number = 6): Calculator[] => {
  return [...calculators]
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
};

export const getNewCalculators = (limit: number = 4): Calculator[] => {
  return calculators
    .filter(calc => calc.isNew)
    .slice(0, limit);
};
